import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Alert, Button, Modal, TextField } from "@yoast/ui-library";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { provisionedSubscriptionShape } from "./provisionedSubscriptionShape";
import * as styles from "./ProvisioningStyles.module.scss";

const messages = defineMessages( {
	title: {
		id: "provisioning.setSite.title",
		defaultMessage: "Connect a site to subscription {number}",
	},
	ariaLabel: {
		id: "provisioning.setSite.ariaLabel",
		defaultMessage: "Connect a site to a provisioned subscription",
	},
	siteLabel: {
		id: "provisioning.setSite.siteLabel",
		defaultMessage: "Site URL",
	},
	description: {
		id: "provisioning.setSite.description",
		defaultMessage: "Connecting a site makes this subscription a valid license for the Yoast plugins installed on that website; the plugin checks it when it is activated. A subscription covers one site, so entering a different URL moves the license to that site. This only matters for plugins: SaaS products outside WordPress and Shopify work from the customer's account and do not need a site.",
	},
	cancel: {
		id: "provisioning.setSite.cancel",
		defaultMessage: "Cancel",
	},
	submit: {
		id: "provisioning.setSite.submit",
		defaultMessage: "Connect site",
	},
	error: {
		id: "provisioning.setSite.error",
		defaultMessage: "Could not set the site. Please try again.",
	},
} );

/**
 * Sets the site a provisioned subscription is connected to. Submits straight away, no confirmation.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The modal.
 */
function SetSiteModal(
	{
		subscription = null,
		isSubmitting = false,
		hasError = false,
		errorMessage = null,
		onCancel,
		onSubmit,
		intl,
	},
) {
	const [ site, setSite ] = useState( "" );

	useEffect( () => {
		setSite( subscription && subscription.siteUrl ? subscription.siteUrl : "" );
	}, [ subscription ] );

	const handleSubmit = ( event ) => {
		event.preventDefault();
		if ( ! site.trim() || isSubmitting ) {
			return;
		}
		onSubmit( { site: site.trim() } );
	};

	return (
		<Modal
			isOpen={ Boolean( subscription ) }
			onClose={ onCancel }
			aria-label={ intl.formatMessage( messages.ariaLabel ) }
		>
			<Modal.Panel>
				<form onSubmit={ handleSubmit }>
					<Modal.Title as="h2" size="2">
						<FormattedMessage
							{ ...messages.title }
							values={ { number: subscription ? subscription.subscriptionNumber : "" } }
						/>
					</Modal.Title>
					<div className={ styles.modalBody }>
						<p>
							<FormattedMessage { ...messages.description } />
						</p>
						<TextField
							id="provisioning-set-site-url"
							name="site"
							type="url"
							label={ intl.formatMessage( messages.siteLabel ) }
							value={ site }
							required={ true }
							disabled={ isSubmitting }
							onChange={ ( event ) => setSite( event.target.value ) }
						/>
						{ hasError && (
							<Alert variant="error">
								{ errorMessage || <FormattedMessage { ...messages.error } /> }
							</Alert>
						) }
					</div>
					<div className={ styles.modalActions }>
						<Button variant="secondary" onClick={ onCancel } disabled={ isSubmitting }>
							<FormattedMessage { ...messages.cancel } />
						</Button>
						<Button
							type="submit"
							variant="primary"
							isLoading={ isSubmitting }
							disabled={ isSubmitting || ! site.trim() }
						>
							<FormattedMessage { ...messages.submit } />
						</Button>
					</div>
				</form>
			</Modal.Panel>
		</Modal>
	);
}

SetSiteModal.propTypes = {
	subscription: provisionedSubscriptionShape,
	isSubmitting: PropTypes.bool,
	hasError: PropTypes.bool,
	errorMessage: PropTypes.string,
	onCancel: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
};

export default injectIntl( SetSiteModal );
